import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import useAuth from '../hooks/useAuth';
import {
  chatAccessToken,
  chatWsBackoffMs,
  chatWsBusy,
  isChatHttpAuthError,
  isChatWsAuthClose,
} from '../utils/chatSocket';

const ChatLiveContext = createContext({
  unreadTotal: 0,
  inboxLive: false,
  useFailover: false,
  subscribe: () => () => {},
  setActiveThreadId: () => {},
  refreshUnread: () => {},
});

const LIVE_ROLES = new Set(['doctor']);
const PING_MS = 25000;
const FAILOVER_MS = 2500;
const UNREAD_POLL_MS = 15000;

const MAX_WS_ATTEMPTS = 8;

const liveWsUrl = (token) => {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/ws/live/?token=${encodeURIComponent(token)}`;
};

const chatLiveRole = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}')?.role;
  } catch (err) {
    return null;
  }
};

export function ChatLiveProvider({ children }) {
  const { isLoggedIn, user } = useAuth();
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [inboxLive, setInboxLive] = useState(false);
  const [useFailover, setUseFailover] = useState(false);
  const [tabHidden, setTabHidden] = useState(
    () => typeof document !== 'undefined' && document.hidden,
  );
  const [liveAuthDead, setLiveAuthDead] = useState(false);
  const listenersRef = useRef(new Set());
  const activeThreadIdRef = useRef(null);
  const role = user?.role || chatLiveRole();
  const enableLive = Boolean(isLoggedIn && LIVE_ROLES.has(role));

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const setActiveThreadId = useCallback((threadId) => {
    const next = threadId == null || threadId === '' ? null : Number(threadId);
    activeThreadIdRef.current = Number.isFinite(next) ? next : null;
  }, []);

  const applyEvent = useCallback((event) => {
    const activeId = activeThreadIdRef.current;
    const alreadyRead = event.event === 'message' && Boolean(
      event.last_message?.read_at || event.last_message?.receipt === 'read'
    );
    const openThread = Boolean(
      activeId
      && event.thread_id
      && Number(event.thread_id) === activeId
      && (event.event === 'message' || event.event === 'receipt' || event.event === 'deleted')
    );
    const skipIncrement = alreadyRead || openThread;
    let badgeDelta = event.unread_delta;
    if (skipIncrement && typeof badgeDelta === 'number' && badgeDelta > 0) {
      badgeDelta = undefined;
    }

    if (typeof event.unread_total === 'number') {
      setUnreadTotal(event.unread_total);
    } else if (typeof badgeDelta === 'number' && badgeDelta !== 0) {
      setUnreadTotal((current) => Math.max(0, current + badgeDelta));
    }
    if (event.event && event.event !== 'hello') {
      const listEvent = skipIncrement
        ? { ...event, unread_delta: undefined, unread_count: 0 }
        : event;
      listenersRef.current.forEach((fn) => fn(listEvent));
    }
  }, []);

  const fetchUnread = useCallback(async () => {
    if (liveAuthDead) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const response = await axios.get('/ws/inbox/unread/');
      const total = Number(response.data?.unread_total) || 0;
      setUnreadTotal(total);
    } catch (err) {
      if (isChatHttpAuthError(err)) {
        setLiveAuthDead(true);
        setUseFailover(false);
      }
    }
  }, [liveAuthDead]);

  useEffect(() => {
    const onVis = () => setTabHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!enableLive) {
      setUnreadTotal(0);
      setInboxLive(false);
      setUseFailover(false);
      setLiveAuthDead(false);
      return undefined;
    }
    if (liveAuthDead) {
      setInboxLive(false);
      setUseFailover(false);
      return undefined;
    }

    let cancelled = false;
    let socket;
    let retryTimer;
    let openTimer;
    let pingTimer;
    let attempt = 0;
    let opened = false;
    let forbidden = false;

    const clearTimers = () => {
      clearTimeout(retryTimer);
      clearTimeout(openTimer);
      clearInterval(pingTimer);
      retryTimer = undefined;
      openTimer = undefined;
      pingTimer = undefined;
    };

    const stopAuth = () => {
      forbidden = true;
      clearTimers();
      setInboxLive(false);
      setUseFailover(false);
      setLiveAuthDead(true);
    };

    const dropSocket = () => {
      const previous = socket;
      socket = null;
      if (!previous) return;
      previous.onopen = null;
      previous.onmessage = null;
      previous.onerror = null;
      previous.onclose = null;
      if (previous.readyState < 2) previous.close();
    };

    const scheduleReconnect = () => {
      if (cancelled || document.hidden || retryTimer || forbidden) return;
      if (attempt >= MAX_WS_ATTEMPTS) {
        setUseFailover(true);
        return;
      }
      const delay = chatWsBackoffMs(attempt);
      attempt += 1;
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled || document.hidden || forbidden) return;
      const token = chatAccessToken();
      if (!token) {
        stopAuth();
        return;
      }
      if (chatWsBusy(socket) || (socket && socket.readyState === WebSocket.CLOSING)) return;
      dropSocket();
      clearInterval(pingTimer);
      pingTimer = undefined;
      let ws;
      try {
        ws = new WebSocket(liveWsUrl(token));
      } catch (err) {
        setInboxLive(false);
        setUseFailover(true);
        scheduleReconnect();
        return;
      }
      socket = ws;
      openTimer = setTimeout(() => {
        if (!cancelled && !opened && !forbidden) setUseFailover(true);
      }, FAILOVER_MS);
      ws.onopen = () => {
        if (cancelled || socket !== ws) return;
        opened = true;
        attempt = 0;
        clearTimeout(openTimer);
        setInboxLive(true);
        setUseFailover(false);
        clearInterval(pingTimer);
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, PING_MS);
      };
      ws.onclose = (ev) => {
        if (socket === ws) socket = null;
        clearInterval(pingTimer);
        pingTimer = undefined;
        if (cancelled) return;
        opened = false;
        setInboxLive(false);
        if (isChatWsAuthClose(ev.code)) {
          stopAuth();
          return;
        }
        setUseFailover(true);
        scheduleReconnect();
      };
      ws.onmessage = (raw) => {
        if (socket !== ws) return;
        let data;
        try {
          data = JSON.parse(raw.data);
        } catch (err) {
          return;
        }
        if (data.type === 'pong') return;
        if (data.type === 'chat.inbox') {
          applyEvent(data);
        }
      };
    };

    const onVisible = () => {
      if (cancelled || document.visibilityState !== 'visible' || forbidden) return;
      if (opened || chatWsBusy(socket) || (socket && socket.readyState === WebSocket.CLOSING)) return;
      attempt = 0;
      clearTimeout(retryTimer);
      retryTimer = undefined;
      setUseFailover(false);
      clearTimeout(openTimer);
      openTimer = setTimeout(() => {
        if (!cancelled && !opened && !forbidden) setUseFailover(true);
      }, FAILOVER_MS);
      connect();
    };

    connect();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearTimers();
      document.removeEventListener('visibilitychange', onVisible);
      setInboxLive(false);
      dropSocket();
    };
  }, [applyEvent, enableLive, liveAuthDead]);

  useEffect(() => {
    if (!enableLive || liveAuthDead || inboxLive || !useFailover || tabHidden) return undefined;
    fetchUnread();
    const pollTimer = setInterval(fetchUnread, UNREAD_POLL_MS);
    return () => clearInterval(pollTimer);
  }, [enableLive, fetchUnread, inboxLive, liveAuthDead, tabHidden, useFailover]);

  const value = useMemo(
    () => ({ unreadTotal, inboxLive, useFailover, subscribe, setActiveThreadId, refreshUnread: fetchUnread }),
    [fetchUnread, inboxLive, setActiveThreadId, subscribe, unreadTotal, useFailover],
  );
  return <ChatLiveContext.Provider value={value}>{children}</ChatLiveContext.Provider>;
}

export const useChatLive = () => useContext(ChatLiveContext);

export default ChatLiveContext;
